import { REMOVED_ACCESS } from '../urlState';

/**
 * Requests for the User Management users list (PD-4063).
 *
 * Everything talks to Management::UsersController, so the paths are the page's
 * own — there is no separate API namespace.
 */

const getJson = async (path) => {
  const response = await fetch(path, {
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.json();
};

const appendValues = (params, key, values) => {
  (values || []).forEach((value) => params.append(`${key}[]`, value));
};

// Coalesced, like createUserService: without a meta tag this would otherwise
// send the header with a non-string value, which differs between fetch
// implementations and bites in tests and any non-Rails context.
const csrfToken = () =>
  document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ??
  '';

const postJson = async (path, body) => {
  const response = await fetch(path, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken(),
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    // A 422 carries the reason the modal shows inline; anything else has no
    // body worth reading and becomes the generic failure.
    const errors = await response
      .json()
      .then((data) => data.errors)
      .catch(() => null);

    if (errors && Object.keys(errors).length) return { errors };

    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.json();
};

const fetchUsers = async ({
  access = null,
  filters = {},
  page = 1,
  query = '',
  sortBy = null,
  sortOrder = null,
} = {}) => {
  const params = new URLSearchParams({ page });

  // Only the removed tab is sent. Current is the server's default too, so
  // spelling it would put a redundant param on every request the page makes.
  if (access === REMOVED_ACCESS) params.set('access', REMOVED_ACCESS);
  if (query) params.set('q', query);

  if (sortBy) {
    params.set('sort_by', sortBy);
    params.set('sort_order', sortOrder || 'ASC');
  }

  appendValues(params, 'account_ids', filters.accountIds);
  appendValues(params, 'permissions', filters.permissions);
  appendValues(params, 'job_roles', filters.jobRoles);

  const data = await getJson(`/management/users.json?${params}`);

  return {
    users: data.users,
    pagination: {
      page: data.pagination.page,
      perPage: data.pagination.per_page,
      totalItems: data.pagination.total_entries,
      totalPages: data.pagination.total_pages,
    },
  };
};

export const fetchFilterOptions = async () => {
  const data = await getJson('/management/users/filter_options.json');

  return {
    // Role names are their own value; job roles already arrive as label/value.
    permissions: data.permissions.map((name) => ({ label: name, value: name })),
    jobRoles: data.jobRoles,
  };
};

/**
 * One page of the Accounts filter menu's options, with the matching total.
 *
 * Paged rather than whole (PD-4577): a large OEM segment runs to hundreds of
 * accounts, and the menu asks for the next page as the user scrolls.
 *
 * @param {string} [query] narrows by account name, server side
 * @param {number} [offset] how many options the menu already holds
 * @returns {Promise<{accounts: Array, total: number}>}
 */
export const fetchAccounts = async (query, offset = 0) => {
  const params = new URLSearchParams();
  if (query) params.set('q', query);
  if (offset) params.set('offset', String(offset));

  const suffix = params.toString() ? `?${params}` : '';
  const data = await getJson(`/management/users/accounts.json${suffix}`);

  return {
    accounts: data.accounts || [],
    // Older responses carried no total; fall back to what arrived rather than
    // reporting zero, which would read as "no accounts". This covers a
    // frontend-first deploy only: a full page with no total then reads as the
    // whole list, and the menu will not page until the backend ships.
    total:
      typeof data.total === 'number'
        ? data.total
        : (data.accounts || []).length,
  };
};

/**
 * Every account the user holds or held, for the Remove / Restore Access modals
 * (PD-4677). The same shape the Access & Permissions tab reads: the caller
 * picks its half by `removedAt`.
 */
export const fetchAccountAccess = async (userId) => {
  const data = await getJson(`/management/users/${userId}/account_access.json`);

  return data.accountAccess || [];
};

export const removeAccess = (userId, accountIds) =>
  postJson(`/management/users/${userId}/remove_access`, {
    account_ids: accountIds,
  });

export const restoreAccess = (userId, accountIds) =>
  postJson(`/management/users/${userId}/restore_access`, {
    account_ids: accountIds,
  });

export default fetchUsers;
